// Простой текст из буфера: Markdown (чат-боты, README), код (из IDE без HTML), таблица из Excel/TSV, текст из PDF
// (переносы строк внутри абзацев и «пере-\nносы» слов склеиваются). Результат - HTML для общего конвейера.

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Похоже ли на Markdown: заголовки #, списки, **жирный**, ``` код, таблицы |---|, $$ формулы, [текст](url). */
export function looksMarkdown(text: string): boolean {
  let score = 0;
  if (/^#{1,6}\s+\S/m.test(text)) score += 2;
  if (/^\s*[-*+]\s+\S/m.test(text)) score += 1;
  if (/^\s*\d+\.\s+\S/m.test(text)) score += 1;
  if (/\*\*[^*\n]+\*\*|__[^_\n]+__/.test(text)) score += 2;
  if (/^```|^~~~/m.test(text)) score += 3;
  if (/^\s*\|?\s*:?-{3,}:?\s*\|/m.test(text)) score += 3;
  if (/\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]/.test(text)) score += 2;
  if (/\$[^$\s][^$\n]*\$/.test(text) && /\\[a-zA-Z]+|[_^]/.test(text)) score += 1;
  if (/\[[^\]\n]+\]\(https?:\/\/[^)\s]+\)/.test(text)) score += 2;
  if (/^>\s+\S/m.test(text)) score += 1;
  if (/\{#(fig|tbl|eq|lst|sec|app):|^Таблица:\s|^Листинг:\s|\[@\w/m.test(text)) score += 3;
  return score >= 3;
}

/** Похоже ли на исходный код (вставка из IDE без HTML). */
export function looksCode(text: string): boolean {
  const lines = text.replace(/\s+$/, '').split(/\r?\n/);
  if (lines.length < 2) return false;
  const nonEmpty = lines.filter((l) => l.trim());
  const codey = nonEmpty.filter((l) => /[;{}]\s*$|^\s{2,}\S|^\t|^\s*(def|class|import|from|#include|return|for|while|if|else|elif|public|private|static|void|int|function|const|let|var|using|namespace|begin|end|SELECT|INSERT|UPDATE|print|echo)\b|=>|::|\(\)\s*[:{]?$/.test(l)).length;
  const prose = nonEmpty.filter((l) => /[А-Яа-яЁё]{3,}[\s,]+[А-Яа-яЁё]{3,}[\s,]+[А-Яа-яЁё]{3,}/.test(l) && !/^\s*(#|\/\/|--|%)/.test(l)).length;
  return codey >= Math.max(2, nonEmpty.length * 0.5) && prose <= nonEmpty.length * 0.2;
}

/** Таблица, разделённая табуляцией (Excel, «копировать как текст»). */
function tsvTable(lines: string[]): string | null {
  const rows = lines.filter((l) => l.length);
  if (rows.length < 2) return null;
  const n = rows[0].split('\t').length;
  if (n < 2 || !rows.every((r) => r.split('\t').length === n)) return null;
  const body = rows.map((r, i) => '<tr>' + r.split('\t').map((c) => (i === 0 ? `<th>${esc(c.trim())}</th>` : `<td>${esc(c.trim())}</td>`)).join('') + '</tr>').join('');
  return `<table><thead>${body.slice(0, body.indexOf('</tr>') + 5)}</thead><tbody>${body.slice(body.indexOf('</tr>') + 5)}</tbody></table>`;
}

const SENT_END = /[.!?:;…»")]$/;
const STARTS_BLOCK = /^([-–—•·▪*]\s|[а-яё]\)\s|\d{1,2}[.)]\s|(Рисунок|Таблица|Листинг|Примечани[ея]|Пример)\s|\d+(\.\d+)*\s+[А-ЯЁ])/;

/** Текст из PDF/веба без разметки -> абзацы: строки внутри абзаца склеиваются, переносы слов убираются. */
export function textToParagraphs(text: string): string[] {
  const src = text.replace(/\r\n?/g, '\n').replace(/\u00ad/g, '').replace(/\f/g, '\n\n');
  const blocks = src.split(/\n[ \t\u00a0]*\n+/);
  const out: string[] = [];
  for (const b of blocks) {
    const lines = b.split('\n').map((l) => l.replace(/[ \t\u00a0]+$/, ''));
    if (!lines.some((l) => l.trim())) continue;
    // PDF: строки примерно одной длины без знака конца предложения - это перенос внутри абзаца
    const lens = lines.filter((l) => l.trim()).map((l) => l.length).sort((a, c) => a - c);
    const typical = lens[Math.floor(lens.length * 0.8)] || 0;
    const wrapped = lines.length >= 3 && typical >= 40;
    let cur = '';
    let prevLen = 0;
    for (const raw of lines) {
      const l = raw.trim();
      if (!l) continue;
      if (!cur) { cur = l; prevLen = raw.length; continue; }
      // новый абзац: не PDF-перенос, маркер/подпись, конец предложения на короткой строке,
      // или короткая строка-подпись/пункт («Рисунок 2 – …», «- пункт») перед строкой с прописной буквы
      const curBlock = STARTS_BLOCK.test(cur) && prevLen < typical * 0.85 && /^[А-ЯЁA-Z«]/.test(l);
      const brk = !wrapped || STARTS_BLOCK.test(l) || curBlock || (SENT_END.test(cur) && prevLen < typical * 0.85);
      if (brk) { out.push(cur); cur = l; } else {
        cur = /[A-Za-zА-Яа-яЁё]-$/.test(cur) && /^[а-яёa-z]/.test(l) ? cur.slice(0, -1) + l : cur + ' ' + l;
      }
      prevLen = raw.length;
    }
    if (cur) out.push(cur);
  }
  return out;
}

/** Простой текст -> HTML: таблица TSV, код, абзацы. */
export function plainTextToHtml(text: string): string {
  const t = text.replace(/\r\n?/g, '\n');
  const tsv = tsvTable(t.replace(/\n+$/, '').split('\n'));
  if (tsv) return tsv;
  if (looksCode(t)) return `<pre>${esc(t.replace(/\n+$/, ''))}</pre>`;
  // формулы в тексте (ChatGPT, Telegram, Markdown): $…$, \(…\) - строчные; \[…\], $$…$$ отдельной строкой - выключные
  const { text: tt, maths } = mathTokens(t);
  const attr = (s: string) => esc(s).replace(/"/g, '&quot;');
  return textToParagraphs(tt).map((p) => {
    const d = p.match(/^\u0000D(\d+)\u0000$/);
    if (d) return `<div data-type="formula" data-latex="${attr(maths[Number(d[1])])}"></div>`;
    return `<p>${esc(p).replace(/\u0000I(\d+)\u0000/g, (_m, k) => `<span data-type="math-inline" data-latex="${attr(maths[Number(k)])}"></span>`)}</p>`;
  }).join('');
}

/** Разделители формул LaTeX в тексте -> метки \u0000D<n>\u0000 (выключная, отдельным абзацем) и \u0000I<n>\u0000
 *  (строчная). «$» денежных сумм не трогается: после открывающего «$» и перед закрывающим - не пробел, после - не цифра. */
export function mathTokens(text: string): { text: string; maths: string[] } {
  const maths: string[] = [];
  const put = (latex: string, display: boolean) => {
    maths.push(latex.trim());
    return display ? `\n\n\u0000D${maths.length - 1}\u0000\n\n` : `\u0000I${maths.length - 1}\u0000`;
  };
  let t = text;
  t = t.replace(/\\\[([\s\S]+?)\\\]/g, (_m, x) => put(x, true));
  t = t.replace(/\$\$([\s\S]+?)\$\$/g, (_m, x) => put(x, true));
  t = t.replace(/\\begin\{(equation|align|gather)\*?\}([\s\S]+?)\\end\{\1\*?\}/g, (m) => put(m, true));
  t = t.replace(/\\\((.+?)\\\)/g, (_m, x) => put(x, false));
  t = t.replace(/(^|[^\\$\w])\$(?![\s$])([^$\n]*?[^\s\\$])\$(?![\d\w])/g, (_m, pre, x) => pre + put(x, false));
  return { text: t, maths };
}

/** Для Markdown-пути: \(…\) -> $…$, \[…\] -> $$…$$ (writer понимает только долларовые разделители). */
export function dollarDelims(text: string): string {
  return text.replace(/\\\[([\s\S]+?)\\\]/g, (_m, x) => `\n$$${x.trim()}$$\n`).replace(/\\\((.+?)\\\)/g, (_m, x) => `$${x.trim()}$`);
}
